/**
 * src/types/charts/bar-stacked.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Stacked bar chart — segments stacked per category, colored by series.
 *
 * DATA MODEL:
 *   Each data point is one SEGMENT of one stacked bar.
 *   `category` identifies which bar (x-axis position).
 *   `series`   identifies which stack layer.
 *
 * Example:
 *   { category: 'Q1', series: 'Product A', value: 42, color: '#6366f1' }
 *   { category: 'Q1', series: 'Product B', value: 28, color: '#f59e0b' }
 */

import type { ChartDataPoint } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_AXES, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';
import type { SchemaNote } from '../chart.schema';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

export type BarStackedAnimationStyle =
  | 'classic' | 'sequential' | 'growUp' | 'layerByLayer' | 'buildUp'
  | 'diagonal' | 'wave' | 'fadeScale' | 'elastic' | 'popIn';

export const BAR_STACKED_ANIMATION_STYLES: BarStackedAnimationStyle[] = [
  'classic', 'sequential', 'growUp', 'layerByLayer', 'buildUp',
  'diagonal', 'wave', 'fadeScale', 'elastic', 'popIn',
];

export const BAR_STACKED_ELEMENT_PANEL_STYLES: BarStackedAnimationStyle[] =
  BAR_STACKED_ANIMATION_STYLES;


// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

export interface BarStackedDataPoint extends ChartDataPoint {
  chartType:  'bar-stacked';
  category:   string;    // x-axis group identifier (bar position)
  series:     string;    // stack layer identifier (determines color + legend entry)
  value:      number;    // segment height. Must be >= 0.
  showValue?: boolean;   // show value label inside segment. Default: true (if tall enough).
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface BarStackedSpacingConfig {
  chartType:       'bar-stacked';
  barWidth:        number;    // px width of each stacked bar. Range: 10–400. Default: 180.
  barSpacing:      number;    // gap between stacked bars. Range: 0–300. Default: 60.
  showTotalLabel:  boolean;   // show sum total above each bar (standard mode only). Default: true.
  normalize:       boolean;   // 100% stacked mode — each bar spans full height. Default: false.
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const BAR_STACKED_DEFAULTS: ChartConfigBase<BarStackedSpacingConfig> = {
  chartType: 'bar-stacked',
  content: {
    titles:    [makeTitle('Revenue Breakdown')],
    subtitles: [makeSubtitle('By Product and Quarter')],
    infoBoxes: [],
  },
  animation:  { duration: 6, style: 'growUp', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout: {
    chartPosition: { x: 0, y: 0 },
    chartMargin:   { top: 200, right: 80, bottom: 120, left: 100 },
  },
  axes:       { ...DEFAULT_AXES },
  legend:     { ...DEFAULT_LEGEND },
  dataLabels: { ...DEFAULT_DATA_LABELS },
  spacing: {
    chartType:      'bar-stacked',
    barWidth:       180,
    barSpacing:     60,
    showTotalLabel: true,
    normalize:      false,
  },
  export: { quality: '1080p', isLooping: true, canvasFormat: '16:9' },
};


// ─────────────────────────────────────────────────────────────────────────────
// SCHEMA NOTES — consumed by chart.schema.ts merger and getChartSchemaSlice()
// ─────────────────────────────────────────────────────────────────────────────

export const BAR_STACKED_SCHEMA_NOTES: Record<string, SchemaNote> = {

  'bar-stacked.barWidth': {
    description: 'Width of each stacked bar column in canvas pixels.',
    type:    'number',
    range:   '10–400',
    default: 180,
    unit:    'canvas pixels',
    affects: ['bar column width', 'total chart width'],
    bounds:  ['Min 10. Max 400.'],
    synergizes: ['bar-stacked.barSpacing — together control column density'],
    tensions:   ['category count — wide bars with many categories overflow chart width'],
    agentNote:  "Default 180px fits ~6 categories comfortably. For 8+ categories reduce to 100–140px. For 12+ reduce to 60–80px.",
  },

  'bar-stacked.barSpacing': {
    description: 'Gap between stacked bar columns in canvas pixels.',
    type:    'number',
    range:   '0–300',
    default: 60,
    unit:    'canvas pixels',
    affects: ['inter-column gap'],
    bounds:  ['Min 0. Max 300.'],
    synergizes: ['bar-stacked.barWidth'],
    tensions:   [],
    agentNote:  "Controls visual separation between categories. 40–80px is standard. Tighter for many categories.",
  },

  'bar-stacked.normalize': {
    description: 'When true, each column fills to 100% of chart height. Segments show % of category total instead of absolute values.',
    type:    'boolean',
    range:   'true | false',
    default: false,
    unit:    'boolean',
    affects: ['segment heights', 'Y axis labels (0–100%)', 'total labels hidden'],
    bounds:  [],
    synergizes: [],
    tensions:   ['bar-stacked.showTotalLabel — total labels are hidden in normalize mode (all totals = 100%)'],
    agentNote:  "Use normalize mode when the story is about composition/proportion, not absolute magnitude. When normalize=true, Y axis shows 0–100% and total labels are suppressed.",
  },

  'bar-stacked.showTotalLabel': {
    description: 'Show the sum total value above each stacked bar column. Only visible in standard (non-normalized) mode.',
    type:    'boolean',
    range:   'true | false',
    default: true,
    unit:    'boolean',
    affects: ['total value label above each column'],
    bounds:  [],
    synergizes: [],
    tensions:   ['bar-stacked.normalize — disabled when normalize=true'],
    agentNote:  "Helpful for comparing absolute totals across categories while still showing composition. Disable if the Y axis already communicates totals clearly.",
  },

  'dataPoint.bar-stacked.category': {
    description: 'Category identifier — determines which column this segment belongs to. All segments with the same category form one stacked bar.',
    type:    'string',
    range:   'any string',
    default: null,
    unit:    'string',
    affects: ['column grouping', 'X axis label'],
    bounds:  ['Must match at least one other point with the same category for stacking to be visible.'],
    synergizes: ['dataPoint.bar-stacked.series — together form the category×series grid'],
    tensions:   [],
    agentNote:  "Categories determine column order (insertion order of first occurrence). Data does NOT need to be sorted — the renderer groups by category automatically.",
  },

  'dataPoint.bar-stacked.series': {
    description: 'Series identifier — determines which layer this segment occupies within its column. Draw order is insertion order of first occurrence (bottom to top).',
    type:    'string',
    range:   'any string',
    default: null,
    unit:    'string',
    affects: ['stack layer position', 'legend entry', 'segment color'],
    bounds:  [],
    synergizes: ['dataPoint.bar-stacked.category'],
    tensions:   [],
    agentNote:  "Series order = insertion order of first occurrence. The first series encountered becomes the bottom layer. Ensure consistent series naming across all categories — a typo creates a phantom layer.",
  },

};
